import { useDefaultValues } from '@aibees/elephant/components/company-similarity-search/hooks/use-default-values';
import { PersonReadDataProvider } from '@aibees/elephant/context';
import { useDataViewChartView } from '@aibees/elephant/context/data-view-context';
import { DataViewProvider } from '@aibees/elephant/context/data-view-context/provider';
import { useSearchFiltersStore } from '@aibees/elephant/feature/search-filters/store';
import SearchTableCompany from '@aibees/elephant/feature/search-table-company/search-table-company';
import { SearchTablePeople } from '@aibees/elephant/feature/search-table-people';
import { useSearchPersonStore } from '@aibees/elephant/feature/search-table-people/store';
import {
  useSearchModeFromRoute,
  useSearchTypeFromRoute,
} from '@aibees/elephant/hooks';
import { useIsPeopleLookalikeEnabled } from '@aibees/elephant/hooks/people';
import { SearchMode, SearchType } from '@aibees/elephant/types';
import { LoadingScreen } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Tooltip } from '@mui/material';
import { lazy, memo, Suspense, useRef } from 'react';
import { SearchBar } from '../search-bar';
import * as Styled from './styles';
import type { SearchResultProps as Props } from './types';
import { useClickOutside } from './use-click-outside';
const SearchDrawer = lazy(() =>
  import(
    /* vitePrefetch: true */ '@aibees/elephant/feature/search-drawer'
  ).then((module) => ({
    default: module.SearchDrawer,
  }))
);

const PeopleDrawer = lazy(() =>
  import(
    /* vitePrefetch: true */ '@aibees/elephant/feature/search-table-people/components/people-drawer'
  ).then((module) => ({ default: module.PeopleDrawer }))
);

const SearchResultInner = memo(({ className }: Props) => {
  const { id, setId } = useSearchPersonStore();
  const type = useSearchTypeFromRoute();
  const mode = useSearchModeFromRoute();
  const isPeopleLookalikeEnabled = useIsPeopleLookalikeEnabled();
  const { isChartView } = useDataViewChartView();

  let table = <SearchTableCompany />;
  if (type === SearchType.People) table = <SearchTablePeople />;

  const items = useDefaultValues();

  const companyDrawerRef = useRef<HTMLDivElement>(null);
  const peopleDrawerRef = useRef<HTMLDivElement>(null);
  const setSelectedItemId = useSearchFiltersStore.useSetSelectedItemId();
  const selectedCompanyId = useSearchFiltersStore.useSelectedItemId?.();
  const isDrawerOpen = Boolean(id || selectedCompanyId);

  useClickOutside(
    [
      { ref: companyDrawerRef, handler: () => setSelectedItemId?.(undefined) },
      { ref: peopleDrawerRef, handler: () => setId(undefined) },
    ],
    isDrawerOpen
  );

  return (
    <Styled.Wrapper className={classnames('SearchResult', className)}>
      <Tooltip
        title={
          mode === SearchMode.Similarity && items.length === 5
            ? `You can add up to 5 Lookalike ${
                type === SearchType.People ? 'People' : 'Domain'
              } from within the Search Results by clicking on the " + Lookalike" field.`
            : null
        }
        placement="top-start"
      >
        <div>
          <SearchBar compact />
        </div>
      </Tooltip>
      <Styled.Content
        hasSearchBar={isPeopleLookalikeEnabled || type === SearchType.Company}
        isChartView={isChartView}
      >
        {table}
        <Suspense fallback={<LoadingScreen />}>
          {type === SearchType.People ? (
            <div ref={peopleDrawerRef}>
              <PersonReadDataProvider id={id}>
                <PeopleDrawer />
              </PersonReadDataProvider>
            </div>
          ) : (
            <div ref={companyDrawerRef}>
              <SearchDrawer />
            </div>
          )}
        </Suspense>
      </Styled.Content>
    </Styled.Wrapper>
  );
});

SearchResultInner.displayName = 'SearchResultInner';

export const SearchResult = memo(({ className }: Props) => {
  return (
    <DataViewProvider>
      <SearchResultInner className={className} />
    </DataViewProvider>
  );
});

SearchResult.displayName = 'SearchResult';
